/**
 * The Yummy mark, as a data URL for the middle of a printed table sticker.
 *
 * Inlined rather than served from `public/` for two reasons. The rendered code
 * is shown through an `<img>`, and an SVG loaded that way is not allowed to
 * fetch anything external — a referenced file would simply not appear. And the
 * sticker has to print correctly on a counter iPad that may be offline, where a
 * second network request for a logo is one more thing that can fail.
 *
 * The letter is knocked out of a solid field rather than drawn in orange on
 * white: a filled counter keeps its edges whatever the printer does with the
 * colour, and it has to hold up at roughly 70 px in the middle of a table card.
 */

/** Brand orange, `--primary` in the shared palette. */
const MARK_ORANGE = '#E85E30';

/** The Y reads as a knock-out, so it is the sticker's own field colour. */
const MARK_INK = '#FFFFFF';

/**
 * The mark itself: a solid orange tile with a white Y, filling a 100×100 box.
 *
 * A rounded square rather than a disc, and it runs to the edges of the box
 * because `qr-code-styling` clears a square of modules behind whatever image it
 * is given. A disc inscribed in that square leaves its four corners cleared and
 * empty — modules spent on white space. Filling the box spends them on the
 * mark instead, which is also what makes the badge read as a tile the code was
 * built around rather than a coin dropped on top of it.
 *
 * The Y is a filled outline, not a stroked line: strokes carry round or angled
 * terminals, and a letter wants flat horizontal cuts across the top of both
 * arms. Drawing the outline means the glyph is a letterform at any size instead
 * of a shape that merely suggests one.
 */
const YUMMY_MARK_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">',
  `<rect x="0" y="0" width="100" height="100" rx="28" fill="${MARK_ORANGE}"/>`,
  /*
   * One closed outline, not an arm shape plus a stem rectangle. Two overlapping
   * subpaths are only a union while they wind the same way: reversed, the
   * nonzero fill rule adds +1 and -1 across the overlap and cancels it to zero,
   * which punched an orange hole straight through the junction of the letter —
   * a code that still scanned, with a Y that was not a Y.
   */
  `<path fill="${MARK_INK}" d="M26 26 L44 52 V76 H56 V52 L74 26 H59 L50 38 L41 26 Z"/>`,
  '</svg>',
].join('');

/**
 * {@link YUMMY_MARK_SVG} as a data URL.
 *
 * Percent-encoded rather than base64: it keeps the payload readable in a
 * devtools inspector, and it escapes the `#` in the colour, which would
 * otherwise end the URL at a fragment.
 */
export const YUMMY_MARK = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(YUMMY_MARK_SVG)}`;
